import { API_TIMEOUT, getBaseUrl, getInitData, getToken } from './config';
import logger from './logger';
const baseOptions: RequestInit = {
  headers: {
    'Content-Type': 'application/json',
    'Authorization': `tma ${getInitData()}`
  }
};
export interface ApiResponse<T = any> {
  data?: T;
  error?: string;
  success: boolean;
}
export interface DictionaryCategory {
  name: string;
  emoji: string;
}
export interface WordExample {
  sentence: string;
  pinyin: string;
  translation: string;
}
export interface Word {
  word: string;
  pinyin: string;
  translation: string;
  priority: number;
  example?: WordExample;
}
export type WordStatus = 'IN_PROGRESS' | 'LEARNED' | 'BLACK_LIST';
export interface WordWithStatus {
  word: Word;
  status: WordStatus;
  categories: DictionaryCategory[];
}
export const fetchData = async <T>(endpoint: string): Promise<ApiResponse<T>> => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT);
    const response = await fetch(`${getBaseUrl()}${endpoint}`, {
      ...baseOptions,
      method: 'GET',
      signal: controller.signal
    });
    clearTimeout(timeoutId);
    if (!response.ok) {
      return { success: false };
    }
    let data;
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      const text = await response.text();
      data = text ? JSON.parse(text) : null;
    } else {
      data = null;
    }
    return { data, success: true };
  } catch (error) {
    return { success: false };
  }
};
export const postData = async <T>(endpoint: string, body: any): Promise<ApiResponse<T>> => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT);
    const response = await fetch(`${getBaseUrl()}${endpoint}`, {
      ...baseOptions,
      method: 'POST',
      body: JSON.stringify(body),
      signal: controller.signal
    });
    clearTimeout(timeoutId);
    if (!response.ok) {
      return { success: false };
    }
    let data;
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      const text = await response.text();
      data = text ? JSON.parse(text) : null;
    } else {
      data = null;
    }
    return { data, success: true };
  } catch (error) {
    return { success: false };
  }
};
export const putData = async <T>(endpoint: string, body: any): Promise<ApiResponse<T>> => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT);
    const response = await fetch(`${getBaseUrl()}${endpoint}`, {
      ...baseOptions,
      method: 'PUT',
      body: JSON.stringify(body),
      signal: controller.signal
    });
    clearTimeout(timeoutId);
    if (!response.ok) {
      return { success: false };
    }
    let data;
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      const text = await response.text();
      data = text ? JSON.parse(text) : null;
    } else {
      data = null;
    }
    return { data, success: true };
  } catch (error) {
    return { success: false };
  }
};
export const fetchDictionaryCategories = async (): Promise<ApiResponse<DictionaryCategory[]>> => {
  return fetchData<DictionaryCategory[]>('/dictionary/categories');
};
export const fetchCategoryWords = async (categoryName: string): Promise<ApiResponse<WordWithStatus[]>> => {
  return fetchData<WordWithStatus[]>(`/dictionary/categories/${categoryName}/words`);
};
interface SearchResponse {
  content: Word[];
  totalPages: number;
  totalElements: number;
  pageable: {
    pageNumber: number;
  };
}
export const searchWords = async (query: string, page: number = 0, size: number = 20): Promise<ApiResponse<SearchResponse>> => {
  return getData<SearchResponse>(`/words?input=${encodeURIComponent(query)}&page=${page}&size=${size}`);
};
export const addWordToCategory = async (word: Word, categoryName: string): Promise<ApiResponse<any>> => {
  return postData<any>(`/dictionary/words`, {
    word: word.word,
    category: categoryName,
    status: 'IN_PROGRESS'
  });
};
export const fetchCombinations = async (characters: string[]): Promise<ApiResponse<Word[]>> => {
  return postData<Word[]>(`/words/combinations`, {
    characters: characters
  });
};
export const fetchWordsCount = async (status: WordStatus): Promise<ApiResponse<number>> => {
  return fetchData<number>(`/dictionary/words/count?status=${status}`);
};
export const updateWordStatus = async (word: string, status: WordStatus): Promise<ApiResponse<any>> => {
  return postData<any>(`/dictionary/words`, {
    word: word,
    status: status
  });
};
export const updateCategory = async (oldName: string, newName: string, emoji: string): Promise<ApiResponse<any>> => {
  return putData<any>(`/dictionary/categories/${oldName}`, {
    name: newName,
    emoji: emoji
  });
};
export const deleteCategory = async (categoryName: string): Promise<ApiResponse<any>> => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT);
    const response = await fetch(`${getBaseUrl()}/dictionary/categories/${categoryName}`, {
      ...baseOptions,
      method: 'DELETE',
      signal: controller.signal
    });
    clearTimeout(timeoutId);
    if (!response.ok) {
      return { success: false };
    }
    let data;
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      const text = await response.text();
      data = text ? JSON.parse(text) : null;
    } else {
      data = null;
    }
    return { data, success: true };
  } catch (error) {
    return { success: false };
  }
};
export const sendChatMessage = async (chatId: string, message: string, onChunk: (chunk: string) => void): Promise<void> => {
  try {
    const response = await fetch(`${getBaseUrl()}/ai/chat/${chatId}/messages`, {
      ...baseOptions,
      method: 'POST',
      headers: {
        ...baseOptions.headers,
        'Authorization': `tma ${getInitData()}`
      },
      body: JSON.stringify({ message })
    });
    
    if (!response.ok) {
      return;
    }

    const reader = response.body?.getReader();
    if (!reader) {
      return;
    }

    const decoder = new TextDecoder();
    let buffer = '';
    
    while (true) {
      const { done, value } = await reader.read();
      // console.log(`done: '${done}', value: '${value}'`);

      if (done) {
        const match = buffer.match(/^data:(.*)$/m);
        if (match) {
          onChunk(match[1]);
        }
        break;
      }
      
      buffer += decoder.decode(value, { stream: true });
      // console.log(`buffer: '${buffer}'`);
      const lines = buffer.split('\n');
      // console.log(`lines: '${lines}'`);
      buffer = lines.pop() || '';
      
      for (const line of lines) {
        // console.log(`line: '${line}'`);
        
        const match = line.match(/^data:([\s\S]*)$/);
        if (match) {
          onChunk(match[1]);
        }
      }
    }
  } catch (error) {
    // Не отображаем ошибку
  }
};
export const getData = async <T>(endpoint: string): Promise<ApiResponse<T>> => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT);
    const response = await fetch(`${getBaseUrl()}${endpoint}`, {
      ...baseOptions,
      method: 'GET',
      signal: controller.signal
    });
    clearTimeout(timeoutId);
    if (!response.ok) {
      return { success: false };
    }
    let data;
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      const text = await response.text();
      data = text ? JSON.parse(text) : null;
    } else {
      data = null;
    }
    return { data, success: true };
  } catch (error) {
    return { success: false };
  }
};
export const getUniqueCategoryName = async (baseName: string): Promise<string> => {
  try {
    const response = await fetchDictionaryCategories();
    if (response.success && response.data) {
      const existingCategories = response.data.map((cat: DictionaryCategory) => cat.name);
      let newName = baseName;
      let counter = 1;
      
      while (existingCategories.includes(newName)) {
        newName = `${baseName} ${counter}`;
        counter++;
      }
      
      return newName;
    }
    return baseName;
  } catch (err) {
    logger.error('Ошибка при проверке существующих категорий:', err);
    return baseName;
  }
}; 